import React, { useId } from 'react';
import { UncontrolledTooltip } from 'reactstrap';
import '../common/tooltip.scss';

// Import the icon maps from the parent component
// Note: In a real implementation, you'd want to move these to a shared utilities file
import FilterIcon from '../../assets/icons/top-bar-icons/filter.svg?react';
import ForwardIcon from '../../assets/icons/top-bar-icons/forward.svg?react';
import GoBackIcon from '../../assets/icons/top-bar-icons/go-back.svg?react';
import MoveToIcon from '../../assets/icons/top-bar-icons/move-to.svg?react';
import NextLg from '../../assets/icons/top-bar-icons/next-lg.svg?react';
import PrevLg from '../../assets/icons/top-bar-icons/prev-lg.svg?react';
import PrintIcon from '../../assets/icons/top-bar-icons/print.svg?react';
import RefreshIcon from '../../assets/icons/top-bar-icons/refresh.svg?react';
import ReplyIcon from '../../assets/icons/top-bar-icons/respond.svg?react';
import ReplyAllIcon from '../../assets/icons/top-bar-icons/respond-all.svg?react';
import TrashIcon from '../../assets/icons/top-bar-icons/trash.svg?react';
import UnreadIcon from '../../assets/icons/top-bar-icons/unread.svg?react';
import FolderIcon from '../../assets/icons/folder.svg?react';
import AngleLeftIcon from '../../assets/icons/top-bar-icons/left.svg?react';

// Disabled icons
import FilterIconDisabled from '../../assets/icons/top-bar-icons/disabled/filter.svg?react';
import ForwardIconDisabled from '../../assets/icons/top-bar-icons/disabled/forward.svg?react';
import GoBackIconDisabled from '../../assets/icons/top-bar-icons/disabled/go-back.svg?react';
import MoveToIconDisabled from '../../assets/icons/top-bar-icons/disabled/move-to.svg?react';
import NextLgDisabled from '../../assets/icons/top-bar-icons/disabled/next-lg.svg?react';
import PrevLgDisabled from '../../assets/icons/top-bar-icons/disabled/prev-lg.svg?react';
import PrintIconDisabled from '../../assets/icons/top-bar-icons/disabled/print.svg?react';
import RefreshIconDisabled from '../../assets/icons/top-bar-icons/disabled/refresh.svg?react';
import ReplyIconDisabled from '../../assets/icons/top-bar-icons/disabled/respond.svg?react';
import ReplyAllIconDisabled from '../../assets/icons/top-bar-icons/disabled/respond-all.svg?react';
import TrashIconDisabled from '../../assets/icons/top-bar-icons/disabled/trash.svg?react';
import UnreadIconDisabled from '../../assets/icons/top-bar-icons/disabled/unread.svg?react';
import AngleLeftIconDisabled from '../../assets/icons/top-bar-icons/disabled/left.svg?react';
import AddIcon from '../../assets/icons/add.svg?react';

import './toolbar-action.scss';

const iconMap = {
  'lf-icon-reply': ReplyIcon,
  'lf-icon-reply-all': ReplyAllIcon,
  'lf-icon-send': ForwardIcon,
  'lf-icon-mail-open': UnreadIcon,
  'lf-icon-print': PrintIcon,
  'lf-icon-trash': TrashIcon,
  'lf-icon-filter': FilterIcon,
  'lf-icon-forward': ForwardIcon,
  'lf-icon-go-back': GoBackIcon,
  'lf-icon-move-to': MoveToIcon,
  'lf-icon-next-lg': NextLg,
  'lf-icon-prev-lg': PrevLg,
  'lf-icon-refresh': RefreshIcon,
  'lf-icon-add': AddIcon,
  'lf-icon-angle-left': AngleLeftIcon,
  'lf-icon-folder': FolderIcon
};

const iconMapDisabled = {
  'lf-icon-reply': ReplyIconDisabled,
  'lf-icon-reply-all': ReplyAllIconDisabled,
  'lf-icon-send': ForwardIconDisabled,
  'lf-icon-mail-open': UnreadIconDisabled,
  'lf-icon-print': PrintIconDisabled,
  'lf-icon-trash': TrashIconDisabled,
  'lf-icon-filter': FilterIconDisabled,
  'lf-icon-forward': ForwardIconDisabled,
  'lf-icon-go-back': GoBackIconDisabled,
  'lf-icon-move-to': MoveToIconDisabled,
  'lf-icon-next-lg': NextLgDisabled,
  'lf-icon-prev-lg': PrevLgDisabled,
  'lf-icon-refresh': RefreshIconDisabled,
  'lf-icon-angle-left': AngleLeftIconDisabled,
  'lf-icon-folder': FolderIcon,
};

const getToolbarIcon = (
  iconParam,
  disabled = false,
  className = '',
  style = {}
) => {
  const currentIconMap = disabled ? iconMapDisabled : iconMap;

  if (iconParam && iconParam.includes('lf-icon')) {
    const iconClasses = iconParam.split(' ');
    const specificIconClass = iconClasses.find(
      cls => cls.startsWith('lf-icon-') && cls !== 'lf-icon'
    );

    if (specificIconClass && currentIconMap[specificIconClass]) {
      const IconComponent = currentIconMap[specificIconClass];
      return (
        <IconComponent className={className} style={style} aria-hidden="true" />
      );
    }

    if (disabled && !iconParam.includes('-disabled')) {
      return `${iconParam} ${iconParam.replace('lf-icon', 'lf-icon-disabled')}`;
    }
    return iconParam;
  }

  if (iconParam && currentIconMap[`lf-icon-${iconParam}`]) {
    const IconComponent = currentIconMap[`lf-icon-${iconParam}`];
    return (
      <IconComponent className={className} style={style} aria-hidden="true" />
    );
  }

  return iconParam || '';
};

const ToolBarAction = ({
  id = '',
  onClick = () => null,
  disabled = false,
  icon,
  title = '',
  className,
  color = 'default',
  allowTextHidden = true,
  hideText = false
}) => {
  // Get the appropriate icon based on disabled state
  const generatedId = `toolbar-action-${useId().replace(/:/g, '')}`;
  const buttonId = id || generatedId;
  const resolvedIcon = getToolbarIcon(icon, disabled);

  return (
    <>
      <button
        id={buttonId}
        onClick={onClick}
        disabled={disabled}
        className={`${'btn-toolbar-action'} ${className || ''} ${
          disabled && 'btn-toolbar-action-disabled'
        } ${`btn-toolbar-action-${color}`}`}
      >
        {resolvedIcon &&
          (React.isValidElement(resolvedIcon) ? (
            resolvedIcon
          ) : (
            <i className={resolvedIcon} />
          ))}
        <span className={allowTextHidden && hideText ? 'd-none' : ''}>{title}</span>
      </button>
      {title && (
       <UncontrolledTooltip
         target={buttonId}
         placement="bottom"
         popperClassName="lf-tooltip"
       >
         {title}
       </UncontrolledTooltip>
      )}
    </>
  );
};

export default ToolBarAction;
